import { FC, useContext, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { Outlet } from "react-router-dom";
import { LogoutState } from "@/components/sync/types";
import { electronModule } from "@/modules/electron";
import { useAppStore, usePublicAppStore } from "@/store";
import { FloatingAgentWindowGlobalStyles } from "@/pages/floating-agent/floating-agent-window-chrome";
import { AppStoreContext } from "@/store/utils/context";

/**
 * Auth and rollout gate for the floating-agent widget window. Unlike
 * RequireStandardModeOverlayOutlet, this must never navigate to the login page:
 * the host is a frameless, non-resizable widget window, so ordinary app UI
 * would be unusable there. Ineligible sessions render only transparent chrome;
 * the main renderer owns the configuration push that tears the window down.
 */
export const FloatingAgentRouteOutlet: FC = observer(() => {
  const { publicStore } = usePublicAppStore();
  const privateStore = useContext(AppStoreContext);
  const hasSufficientAuth = publicStore.auth.isStandardMode;
  const logoutState = privateStore?.store.sync.logoutState;
  const isLogoutSuppressed =
    logoutState === LogoutState.LoggingOut || logoutState === LogoutState.LoggedOut;
  const shouldSuppressWidget = !hasSufficientAuth || isLogoutSuppressed;

  useEffect(() => {
    if (!shouldSuppressWidget) return;

    void electronModule.floatingAgent.hide();
  }, [shouldSuppressWidget]);

  if (shouldSuppressWidget) {
    return <FloatingAgentWindowGlobalStyles />;
  }

  return <FloatingAgentAccessOutlet />;
});

const FloatingAgentAccessOutlet: FC = observer(() => {
  const { store } = useAppStore();
  const canRenderStandaloneWindow = store.floatingAgent.isStandaloneRuntimeEnabled;

  if (!canRenderStandaloneWindow) {
    return <FloatingAgentWindowGlobalStyles />;
  }

  return <Outlet />;
});
